<template>
  <b-field :label="label">
    <span v-if="options.length === 0">{{ $t("dataTable.no_filter_available") }}</span>
    <!-- #476 - Le dropdown reste accessible meme avec une seule option.
         Le tag unique est non-supprimable (bouton x grise) et une infobulle
         explique pourquoi dans le dropdown. -->
    <b-taginput
      v-else
      v-model="selectedKeys"
      :data="filteredOptions"
      :custom-formatter="getDisplay"
      :placeholder="options.length <= 1 ? '' : placeholder"
      :triggers="['focus']"
      autocomplete
      field="naturalKey.sql"
      :icon="options.length>1?'search':''"
      :icon-right="options.length>1?'angle-down':''"
      open-on-focus
      type="search"
      :append-to-body="true"
      :class="{
        'single-option-filter': options.length <= 1 && selectedKeys.length > 0,
        'scrollable-tags': selectedKeys.length > 6,
      }"
      @select="onSelect"
      @typing="onTyping"
    >
      <template #selected="props">
        <b-tag
          v-for="(tag, index) in props.tags"
          :key="index"
          closable
          :class="{ 'single-option-tag': options.length <= 1 }"
          :title="options.length <= 1 ? $t('dataTable.only-option-available') : ''"
          :aria-label="getDisplayByKey(tag)"
          @close="options.length > 1 ? removeTag(tag) : undefined"
        >
          {{ getDisplayByKey(tag) }}
        </b-tag>
        <LoadingAnimate
          v-if="loading"
          :size="'is-small'"
        />
      </template>
      <template #default="slotProps">
        <OaListOptionTooltip
          :tooltip-label="options.length <= 1 ? $t('dataTable.only-option-available') : getDisplay(slotProps.option)"
          :display-html="highlight(getDisplay(slotProps.option))"
        />
      </template>
    </b-taginput>
  </b-field>
</template>

<script lang="ts">
import { computed, ref, watch } from "vue";
import i18n from "@/i18n";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import OaListOptionTooltip from "@/components/common/OaListOptionTooltip.vue";
import { BField, BIcon, BTag, BTaginput, BTooltip } from "buefy";
import { highlightMatch } from "@/composable/components/highlight";

/**
 * Sentinel objet pour représenter la sélection "(vide)" dans
 * `selectedKeys` sans casser le contrat de b-taginput ( qui attend
 * des objets avec `naturalKey.sql` , cf. `field="naturalKey.sql"` ) .
 *
 * <p>Identifiable par `__isEmptyValue: true` ; sa clé SQL est `null`
 * ce qui correspond exactement au format wire attendu par le backend
 * ( cf. {@code ComponentFiltersByReference} qui accepte les valeurs
 * `null` et `DataRequestBuilder.buildReferencePredicate` qui les
 * traduit en JSONPath `@ == null` ) .
 */
const EMPTY_KEY_SENTINEL = Object.freeze({
  naturalKey: { sql: null },
  __isEmptyValue: true,
});
function isEmptySentinel(key: any): boolean {
  return !!key?.__isEmptyValue;
}

export default {
  name: "ReferenceFilter",
  components: { BField, BIcon, BTaginput, BTag, BTooltip, LoadingAnimate, OaListOptionTooltip },
  props: {
    columns: { type: Object, required: true },
    filterList: {
      type: Object, required: true, default: () => {
      },
    },
    filters: { type: Array, default: () => [] },
    currentFilter: {
      type: Object, default: () => {
      },
    },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    /**
     * Vrai si la colonne FK ( ReferenceChecker ) contient au moins une
     * ligne avec valeur nulle en base . Drapeau remonté par le backend
     * via {@code FilterListEntry.hasEmpty} pour la clé du composant
     * ( cf. §5.7 de FILTER_TEXT_LIST.md ) . Conditionne l'injection
     * de l'entrée virtuelle "(vide)" en tête du dropdown ( pattern
     * unifié avec ListFilter ; remplace l'ancien bouton externe
     * EmptyValueButton pour cohérence UX entre listbox filters ) .
     */
    hasEmpty: { type: Boolean, default: false },
  },
  emits: ["update-reference-filter"],
  setup(props, { emit }) {
    const componentKey = computed(() => props.columns.id);
    const refId = computed(() => props?.columns?.refLinkedTo);
    // PERF audit ( regression fix ) : depuis la parallélisation de
    // /filters et /data ( commit 7c1162d0 ) , ReferenceFilter peut
    // se monter AVANT que `filterList` ne soit peuplé ( si /data
    // résout en premier ) . Avec l'ancienne capture non-réactive
    // `options = props.filterList[...]...`  , `options` restait []
    // à vie même après l'arrivée de /filters -> "aucun filtre
    // disponible" affiché à tort . On passe en computed pour que
    // l'option se met à jour automatiquement lorsque le parent
    // injecte enfin les données .
    const options = computed(() => props?.filterList?.[refId.value]
      ?.filter((node: any) => node.components?.includes(componentKey.value)) || []);
    // #474 - Liste triée de toutes les options ( référence stable ) .
    // Computed dépendant de `options` pour rester en phase avec les
    // arrivées asynchrones de filterList .
    //
    // Ticket UX cohérence "(vide)" : on injecte le sentinel EN TÊTE
    // du dropdown ( pattern identique à ListFilter ) au lieu d'un
    // bouton externe . Condition : hasEmpty=true ET au moins 2 options
    // de référence ( en mode single-option l'auto-select prend la main ,
    // cf. logique #476 ; on conserve le comportement historique du
    // bouton qui était disabled si options.length <= 1 ) .
    const sortedOptions = computed(() => {
      const sorted = [...options.value].sort((a: any, b: any) =>
        (a.naturalKey?.sql || "").localeCompare(b.naturalKey?.sql || ""),
      );
      return (props.hasEmpty && options.value.length > 1)
        ? [EMPTY_KEY_SENTINEL, ...sorted]
        : sorted;
    });
    // Texte saisi pour mettre en evidence dans les options du dropdown
    const searchText = ref("");

    const currentFilters = computed(() => props.filters
      .find(f => f.componentKey === refId.value),
    );
    // Sélection courante . Initialisée vide si options pas encore là ;
    // un watcher ci-dessous ( options ) gère le cas single-option qui
    // arriverait après le mount .
    const selectedKeys = ref<any[]>(
      (options.value.length <= 1)
        ? [...options.value]
        : (currentFilters.value?.filters ? [...currentFilters.value.filters] : []),
    );

    // Synchronisation si currentFilter change
    watch(
      () => props.currentFilter,
      (newVal) => {
        selectedKeys.value = newVal?.filters ? [...newVal.filters] : [];
      },
      { deep: true },
    );
    watch(
      () => props.filterList,
      (newVal) => {
        selectedKeys.value = newVal?.filters ? [...newVal.filters] : [];
      },
      { deep: true },
    );

    // Si filterList arrive APRÈS le mount et qu'on découvre alors qu'on
    // est en mode single-option , on auto-sélectionne tardivement et on
    // émet le filtre ( cf. issue #476 pour la sémantique single-option
    // non-supprimable ) . Garde-fou : on ne touche pas si l'utilisateur
    // a déjà fait une sélection manuelle ( ou si un watcher précédent a
    // restauré une sélection depuis currentFilter ) . Enregistré APRÈS
    // les watchers sur currentFilter et filterList pour passer en
    // dernier dans la file Vue et ne pas se faire écraser par eux .
    watch(options, (newOpts) => {
      if (newOpts.length <= 1 && selectedKeys.value.length === 0) {
        selectedKeys.value = [...newOpts];
        if (newOpts.length === 1) {
          setTimeout(() => sendEvent(), 0);
        }
      }
    }, { flush: "post" });

    // #476 - Émission au montage quand une seule option est déjà
    // présente côté props ( filterList déjà peuplé avant ce mount ) .
    // Le cas arrivant tardivement est géré par le watcher `options`
    // ci-dessus pour ne pas être manqué ( cf. fix régression
    // post-parallélisation /filters + /data ) .
    if (options.value.length === 1) {
      setTimeout(() => sendEvent(), 0);
    }

    // Vrai si le sentinel "(vide)" est dans la sélection courante .
    // Sert uniquement à filteredOptions pour le retirer du dropdown
    // quand il a déjà été choisi ( évite un double-ajout , même règle
    // que pour les références classiques via selectedSqls ) .
    const isEmptyValueSelected = computed(
      () => (selectedKeys.value || []).some(isEmptySentinel),
    );

    function sendEvent() {
      // Mappage uniforme vers le format wire : la clé SQL pour les
      // nodes référence , `null` pour le sentinel "(vide)" . Le backend
      // ( ComponentFiltersByReference ) accepte les deux .
      const filters = selectedKeys.value
        .map(opt => isEmptySentinel(opt) ? null : opt.naturalKey.sql);
      emit("update-reference-filter", {
        columns: props.columns,
        componentKey: props.columns.parentComponentKey ? (props.columns.parentComponentKey + "::" + componentKey.value) : componentKey.value,
        filters,
      });
      // La dropdown est téléportée via append-to-body et calcule sa
      // position à l'ouverture. Quand le conteneur de tags rétrécit ou
      // grandit , la dropdown reste à sa position d'origine , créant un
      // écart avec le champ. Un event resize force Buefy à recalculer.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("resize"));
      }
    }

    // Ajout d'une sélection
    function onSelect() {
      sendEvent();
    }

    // Suppression d'un tag
    function removeTag(tag) {
      selectedKeys.value = selectedKeys.value.filter(t => t != tag);
      sendEvent();
    }

    // Affichage du label internationalisé .
    // Cas particulier : le sentinel "(vide)" rend le label i18n discret
    // ( évite que le fallback `option.naturalKey?.sql` retourne null ) .
    function getDisplay(option) {
      if (isEmptySentinel(option)) return i18n.global.t("filter.empty-value-label");
      return (
        option.__display_fr
        || option.__display_default
        || option.naturalKey?.sql
        || option
      );
    }

    // Affichage du label à partir de la clé . Cas particulier : le
    // sentinel "(vide)" rend le label i18n discret correspondant , sans
    // tenter de résoudre dans `options` ( évite le faux match
    // null === null sur un node sans naturalKey valide ) .
    function getDisplayByKey(key) {
      if (isEmptySentinel(key)) return i18n.global.t("filter.empty-value-label");
      const found = options.value.find(opt => opt.naturalKey === key.naturalKey);
      return getDisplay(found || { naturalKey: { sql: key.naturalKey } });
    }

    // #474 + UX - Liste calculée dynamiquement qui combine :
    //   - le filtrage texte (autocomplétion via searchText),
    //   - l'exclusion des options déjà sélectionnées (selectedKeys).
    // Les options sélectionnées disparaissent de la dropdown tant qu'elles
    // sont présentes en tag ; cliquer la croix d'un tag les réintroduit
    // automatiquement à leur position triée.
    const filteredOptions = computed(() => {
      const selectedSqls = new Set(
        (selectedKeys.value || [])
          .filter((k: any) => !isEmptySentinel(k))
          .map((k: any) => k?.naturalKey?.sql)
          .filter(Boolean),
      );
      const emptyAlreadyTaken = isEmptyValueSelected.value;
      const search = (searchText.value || "").toLowerCase();
      return sortedOptions.value.filter((opt: any) => {
        // Sentinel "(vide)" : règles parallèles aux nodes référence -
        // retiré si déjà sélectionné ; soumis au filtrage texte sur
        // son label i18n ( comportement aligné avec ListFilter , cf.
        // spec utilisateur "ne pas toucher au fonctionnement actuel
        // du list filter sur la recherche" ) .
        if (isEmptySentinel(opt)) {
          if (emptyAlreadyTaken) return false;
          if (!search) return true;
          return (getDisplay(opt) || "").toString().toLowerCase().includes(search);
        }
        if (selectedSqls.has(opt.naturalKey?.sql)) return false;
        if (!search) return true;
        const displayName = (getDisplay(opt) || "").toString().toLowerCase();
        const naturalKey = (opt.naturalKey?.sql || "").toLowerCase();
        return displayName.includes(search) || naturalKey.includes(search);
      });
    });

    // Met à jour le texte de recherche ; filteredOptions réagit automatiquement.
    function onTyping(text: string) {
      searchText.value = text || "";
    }

    // Met en gras le texte saisi dans les options du dropdown
    function highlight(text: string): string {
      return highlightMatch(text, searchText.value);
    }

    return {
      currentFilters,
      options,
      filteredOptions,
      selectedKeys,
      onSelect,
      removeTag,
      getDisplay,
      getDisplayByKey,
      onTyping,
      highlight,
      componentKey,
      refId,
      sendEvent,
    };
  },
};
</script>

<style scoped>
/* Limite la hauteur visible de la liste des tags sélectionnés et rend
   le conteneur scrollable quand la liste dépasse un seuil raisonnable.
   Règle conditionnée par la classe .scrollable-tags ( ajoutée au-delà
   de 6 tags ) pour éviter que Chrome n'affiche une mini-scrollbar
   dans les champs vides.

   Scrollbar VISIBLE ( fine ) pour que l'utilisateur sache qu'il peut
   naviguer dans la liste. scrollbar-gutter: stable réserve l'espace
   en permanence , évitant le décalage de largeur quand la scrollbar
   apparait/disparait ( problème historique du décalage avec la
   dropdown téléportée résolu par la réservation d'espace ). */
.scrollable-tags :deep(.taginput-container) {
  max-height: 9em;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin; /* Firefox - scrollbar fine */
}
.scrollable-tags :deep(.taginput-container)::-webkit-scrollbar {
  width: 8px;
}
.scrollable-tags :deep(.taginput-container)::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}
.scrollable-tags :deep(.taginput-container)::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* #476 - Masquer le champ de recherche quand une seule option est selectionnee
   pour eviter l'affichage sur deux lignes (tag + champ vide) */
.single-option-filter :deep(input) {
  display: none;
}

/* #476 - Bouton (x) visible mais grise et non cliquable quand une seule option */
.single-option-tag :deep(.delete) {
  background-color: #b9bbbb;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
}
</style>
